/**
 * CaseLink / CustomerProfileLink — render children as anchors that deep-link
 * to specific Amazon Connect Cases / Customer Profiles records via the Agent
 * Workspace App Controller SDK. Outside the Connect workspace the click
 * logs and otherwise no-ops.
 */

import type { CSSProperties, MouseEvent, ReactNode } from "react";
import { useAppContext } from "../../contexts/AppContext";
import { getTrackingUrl } from "./Customer360.viewmodel";

const linkStyle: CSSProperties = {
  color: "#006ce0",
  textDecoration: "underline",
  textDecorationColor: "#99cbe4",
  textDecorationThickness: "1px",
  textUnderlineOffset: "2px",
  cursor: "pointer",
  fontWeight: 500,
};

interface BaseLinkProps {
  children: ReactNode;
  style?: CSSProperties;
  title?: string;
  className?: string;
}

interface CaseLinkProps extends BaseLinkProps {
  caseId: string;
}

export function CaseLink({ caseId, children, style, title, className }: CaseLinkProps) {
  const { launchCase } = useAppContext();
  const handleClick = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    void launchCase(caseId);
  };
  return (
    <a
      href="#"
      onClick={handleClick}
      title={title ?? `Open case ${caseId} in Amazon Connect`}
      className={`c360-link ${className ?? ""}`.trim()}
      style={{ ...linkStyle, ...style }}
    >
      {children}
    </a>
  );
}

interface TrackingLinkProps extends Omit<BaseLinkProps, "children"> {
  shipperName?: string | null;
  trackingNumber?: string | null;
  /** Defaults to the trackingNumber if not provided. */
  children?: ReactNode;
}

/**
 * Renders the tracking number as a link to the carrier's tracking page when
 * the shipper is recognized; otherwise renders plain text. Opens in a new
 * tab — these are external URLs, not Connect SDK launches.
 */
export function TrackingLink({
  shipperName,
  trackingNumber,
  children,
  style,
  title,
  className,
}: TrackingLinkProps) {
  const url = getTrackingUrl(shipperName, trackingNumber);
  if (!url) return <>{children ?? trackingNumber}</>;
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      title={title ?? `Track ${trackingNumber} with ${shipperName}`}
      className={`c360-link ${className ?? ""}`.trim()}
      style={{ ...linkStyle, ...style }}
    >
      {children ?? trackingNumber}
    </a>
  );
}

interface CustomerProfileLinkProps extends BaseLinkProps {
  profileId: string;
}

export function CustomerProfileLink({
  profileId,
  children,
  style,
  title,
  className,
}: CustomerProfileLinkProps) {
  const { launchCustomerProfile } = useAppContext();
  const handleClick = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    void launchCustomerProfile(profileId);
  };
  return (
    <a
      href="#"
      onClick={handleClick}
      title={title ?? `Open profile ${profileId} in Amazon Connect Customer Profiles`}
      className={`c360-link ${className ?? ""}`.trim()}
      style={{ ...linkStyle, ...style }}
    >
      {children}
    </a>
  );
}
